/** Who is playing on this device: a random id, a name typed later, settings, personal best. */
const KEY = 'coco.player';
const BEST = 'coco.best';

export interface Player {
  id: string;
  name: string;
  sound: boolean;
  haptics: boolean;
  /** the mode last played */
  mode: 'practice' | 'basic' | 'sloth' | 'og' | 'feed';
  /** signed in to a reserved name: `id` is the account's, `anonId` is this device's own */
  signedIn: boolean;
  anonId: string;
  /** clap at the monkeys with the microphone: 'ask' until decided, then on or off */
  clap: 'ask' | 'on' | 'off';
  /** has this player met a monkey before? the explanation shows once */
  metMonkey: boolean;
  /** earned things that carry between rounds: extras of each throwable, and super pineapples */
  specials: { rock: number; pineapple: number; fish: number; shoe: number; super: number };
}

function newId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function loadPlayer(): Player {
  const id = newId();
  const none = { rock: 0, pineapple: 0, fish: 0, shoe: 0, super: 0 };
  const base: Player = { id, name: '', sound: true, haptics: true, mode: 'sloth', signedIn: false, anonId: id, clap: 'ask', metMonkey: false, specials: { ...none } };
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Player> | null;
    if (saved?.id) return { ...base, ...saved, anonId: saved.anonId ?? saved.id, specials: { ...none, ...(saved.specials ?? {}) } };
  } catch {
    /* no storage */
  }
  savePlayer(base);
  return base;
}

export function savePlayer(p: Player): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* no storage */
  }
}

export interface LocalBest {
  score: number;
  hits: number;
  farthest: number;
  at: number;
}

/** personal best for a mode ('sloth' keeps the original key, so old bests survive) */
export function loadBest(mode: Player['mode'] = 'sloth'): LocalBest | null {
  try {
    return JSON.parse(localStorage.getItem(mode === 'sloth' ? BEST : `${BEST}.${mode}`) ?? 'null') as LocalBest | null;
  } catch {
    return null;
  }
}

/** record a finished round; true if it is a new personal best for that mode (higher is better) */
export function recordBest(mode: Player['mode'], score: number, hits: number, farthest: number): boolean {
  const prev = loadBest(mode);
  const better = !prev || score > prev.score;
  if (better) {
    try {
      localStorage.setItem(mode === 'sloth' ? BEST : `${BEST}.${mode}`, JSON.stringify({ score, hits, farthest, at: Date.now() } satisfies LocalBest));
    } catch {
      /* no storage */
    }
  }
  return better;
}
